/**
 * AcquisitionIntelligence — /v3/intel/acquisition-intel
 *
 * BP-027 workspace tab: seller-centric lead intelligence.
 * Renders the 6 blueprint widgets:
 *   1. AI Acquisition Briefing (Claude, top of page)
 *   2. Motivation Score Distribution
 *   3. Acquisition Pipeline
 *   4. Lead Heat classification
 *   5. Top Opportunities table (with per-lead detail drawer)
 *   6. AI Queue + recalculate control
 *
 * All numbers come from GET /api/v3/acquisition-intel/dashboard.
 * Clicking a lead opens the detail drawer with the full explainability
 * trail (every reason + weight), timeline prediction, contact rec,
 * and Next Best Action button (which POSTs to /{id}/next-action).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Brain, Flame, Loader2, RefreshCw, Sparkles, TrendingUp, X,
  Phone, MessageSquare, Mail, Send, Archive, ChevronRight,
  ShieldCheck, Zap, Clock, Target, ArrowRight,
} from "lucide-react";

// ─────────────────────────────── constants ───────────────────────────────

const HEAT_META = {
  hot:    { label: "HOT",    color: "#DC2626", bg: "bg-red-500/15",     text: "text-red-500",     border: "border-red-500/30" },
  high:   { label: "HIGH",   color: "#EA580C", bg: "bg-orange-500/15",  text: "text-orange-500",  border: "border-orange-500/30" },
  medium: { label: "MEDIUM", color: "#F59E0B", bg: "bg-amber-500/15",   text: "text-amber-500",   border: "border-amber-500/30" },
  low:    { label: "LOW",    color: "#0EA5E9", bg: "bg-sky-500/15",     text: "text-sky-500",     border: "border-sky-500/30" },
  cold:   { label: "COLD",   color: "#64748B", bg: "bg-slate-500/15",   text: "text-slate-500",   border: "border-slate-500/30" },
};

const BAND_META = {
  very_high: { label: "Very High", pct: 100 },
  high:      { label: "High",      pct: 80  },
  medium:    { label: "Medium",    pct: 60  },
  low:       { label: "Low",       pct: 40  },
  very_low:  { label: "Very Low",  pct: 20  },
};

const ACTION_ICON = {
  call_now:            Phone,
  call_this_week:      Phone,
  send_sms:            MessageSquare,
  send_email:          Mail,
  send_offer:          Send,
  send_direct_mail:    Send,
  add_to_drip:         Clock,
  assign_to_specialist: Target,
  archive_cold:        Archive,
  schedule_visit:      Target,
};

// ─────────────────────────────── page ────────────────────────────────────

export default function AcquisitionIntelligence() {
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData]         = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v3/acquisition-intel/dashboard");
      setData(data);
    } catch (err) {
      toast.error("Failed to load Acquisition Intelligence.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const recalculate = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/v3/acquisition-intel/recalculate", {
        include_briefing: true,
      });
      toast.success(`Recalculated ${data.processed ?? 0} properties · ${data.ranked ?? 0} ranked.`);
      await load();
    } catch (err) {
      toast.error("Recalculation failed.");
    } finally { setRefreshing(false); }
  }, [load]);

  const refreshBriefing = useCallback(async () => {
    try {
      await api.post("/v3/acquisition-intel/briefing/refresh");
      toast.success("Briefing refreshed.");
      await load();
    } catch { toast.error("Briefing refresh failed."); }
  }, [load]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="acq-intel-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const top = data?.top_opportunities || [];
  const dist = data?.motivation_distribution || {};
  const heat = data?.heat_counts || {};
  const pipeline = data?.pipeline || [];
  const queue = data?.ai_queue || {};
  const briefing = data?.briefing;

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="acquisition-intel-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-emerald-600">
            <Brain className="w-3.5 h-3.5" />
            BP-027 · Seller Intelligence
          </div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 tracking-tight">Acquisition Intelligence</h1>
          <p className="text-sm text-slate-500 mt-1">
            Every seller graded, ranked, and given a next best action. Explainable by construction.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refreshBriefing}
            data-testid="acq-intel-briefing-refresh"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 text-xs font-semibold px-3 py-2 hover:bg-slate-50 transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" /> Refresh AI Briefing
          </button>
          <button
            type="button"
            onClick={recalculate}
            disabled={refreshing}
            data-testid="acq-intel-recalc"
            className="inline-flex items-center gap-1.5 rounded-lg text-white text-xs font-bold px-3 py-2 disabled:opacity-60 shadow"
            style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #EA580C)" }}
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Recalculate All
          </button>
        </div>
      </div>

      {/* Widget 1 — AI Acquisition Briefing */}
      <BriefingCard briefing={briefing} />

      {/* Widget 4 + 6 top strip — Heat counts + AI Queue */}
      <div className="grid md:grid-cols-4 gap-4">
        <HeatCard heat="hot"    count={heat.hot    || 0} />
        <HeatCard heat="high"   count={heat.high   || 0} />
        <HeatCard heat="medium" count={heat.medium || 0} />
        <QueueCard queue={queue} />
      </div>

      {/* Widget 2 + 3 — Distribution + Pipeline (side-by-side) */}
      <div className="grid lg:grid-cols-2 gap-4">
        <DistributionCard dist={dist} />
        <PipelineCard pipeline={pipeline} />
      </div>

      {/* Widget 5 — Top Opportunities table */}
      <TopOpportunitiesCard items={top} onOpen={setSelectedId} />

      {/* Detail drawer */}
      {selectedId && (
        <SellerDetailDrawer
          propertyId={selectedId}
          onClose={() => setSelectedId(null)}
          onActionLogged={load}
        />
      )}
    </div>
  );
}

/* ─────────────────────────── widget: briefing ─────────────────────────── */

function BriefingCard({ briefing }) {
  if (!briefing) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-amber-50 to-white p-6"
           data-testid="acq-briefing-card">
        <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-600">
          <Zap className="w-3.5 h-3.5" /> Daily briefing
        </div>
        <p className="mt-2 text-sm text-slate-600">
          Run <span className="font-semibold">Recalculate All</span> to generate today&rsquo;s AI briefing.
        </p>
      </div>
    );
  }
  const source = briefing.source === "claude" ? "Claude" : "Deterministic";
  return (
    <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50/60 to-white p-6 shadow-sm"
         data-testid="acq-briefing-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-amber-600">
          <Zap className="w-3.5 h-3.5" /> AI Acquisition Briefing · {briefing.briefing_date}
        </div>
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
          {source}
        </span>
      </div>
      <h2 className="mt-2 text-lg font-bold text-slate-900 leading-snug"
          data-testid="acq-briefing-headline">
        {briefing.headline}
      </h2>
      <div className="mt-3 text-sm leading-relaxed text-slate-700 whitespace-pre-line"
           data-testid="acq-briefing-summary">
        {briefing.summary}
      </div>
    </div>
  );
}

/* ─────────────────────────── widget: heat card ───────────────────────── */

function HeatCard({ heat, count }) {
  const meta = HEAT_META[heat];
  return (
    <div className={`rounded-2xl border ${meta.border} ${meta.bg} p-5`}
         data-testid={`acq-heat-${heat}`}>
      <div className="flex items-center gap-2">
        <Flame className={`w-4 h-4 ${meta.text}`} />
        <span className={`text-[10px] font-black tracking-widest uppercase ${meta.text}`}>
          {meta.label}
        </span>
      </div>
      <div className="mt-3 text-4xl font-bold text-slate-900 tabular-nums">{count}</div>
      <div className="mt-1 text-[11px] text-slate-500 uppercase tracking-widest">Leads today</div>
    </div>
  );
}

/* ─────────────────────────── widget: AI queue ────────────────────────── */

function QueueCard({ queue }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="acq-ai-queue">
      <div className="flex items-center gap-2">
        <Brain className="w-4 h-4 text-emerald-600" />
        <span className="text-[10px] font-black tracking-widest uppercase text-emerald-600">
          AI Queue
        </span>
      </div>
      <div className="mt-3 text-4xl font-bold text-slate-900 tabular-nums">
        {queue.scored ?? 0}
        <span className="text-lg text-slate-400 font-medium"> / {queue.total_properties ?? 0}</span>
      </div>
      <div className="mt-1 text-[11px] text-slate-500 uppercase tracking-widest">
        Properties scored
      </div>
      {queue.pending > 0 && (
        <div className="mt-2 text-[11px] text-amber-600 font-semibold">
          {queue.pending} pending recalculation
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── widget: distribution ────────────────────── */

function DistributionCard({ dist }) {
  const bands = ["very_low", "low", "medium", "high", "very_high"];
  const total = bands.reduce((s, b) => s + (dist[b] || 0), 0);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="acq-distribution">
      <div className="flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-emerald-600" />
        <span className="text-[10px] font-black tracking-widest uppercase text-emerald-600">
          Motivation Distribution
        </span>
      </div>
      <div className="mt-4 space-y-2">
        {bands.map((b) => {
          const n = dist[b] || 0;
          const pct = total ? Math.round((n / total) * 100) : 0;
          return (
            <div key={b} className="flex items-center gap-3">
              <div className="w-20 text-[11px] font-bold uppercase tracking-widest text-slate-500">
                {BAND_META[b].label}
              </div>
              <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full transition-[width] duration-700"
                     style={{
                       width: `${pct}%`,
                       backgroundImage: "linear-gradient(90deg, #F59E0B, #EA580C, #DC2626)",
                     }} />
              </div>
              <div className="w-10 text-right text-[13px] font-bold text-slate-700 tabular-nums">
                {n}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────── widget: pipeline ────────────────────────── */

function PipelineCard({ pipeline }) {
  const total = pipeline.reduce((s, p) => s + p.count, 0);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="acq-pipeline">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-emerald-600" />
        <span className="text-[10px] font-black tracking-widest uppercase text-emerald-600">
          Acquisition Pipeline
        </span>
      </div>
      {total === 0 ? (
        <p className="mt-4 text-sm text-slate-400">No properties in the workspace yet.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {pipeline.map((p) => {
            const pct = Math.round((p.count / total) * 100);
            return (
              <div key={p.stage} className="flex items-center gap-3">
                <div className="w-32 text-[11px] font-bold uppercase tracking-widest text-slate-500">
                  {p.stage.replace(/_/g, " ")}
                </div>
                <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-emerald-500 transition-[width] duration-700"
                       style={{ width: `${pct}%` }} />
                </div>
                <div className="w-10 text-right text-[13px] font-bold text-slate-700 tabular-nums">
                  {p.count}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── widget: top opportunities ───────────────── */

function TopOpportunitiesCard({ items, onOpen }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="acq-top-opportunities">
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-2 text-[10px] font-black tracking-widest uppercase text-emerald-600">
          <TrendingUp className="w-3.5 h-3.5" /> Top Opportunities Today
        </div>
        <span className="text-[11px] font-semibold text-slate-500 tabular-nums">
          {items.length} ranked
        </span>
      </div>
      {items.length === 0 ? (
        <p className="mt-5 text-sm text-slate-400">
          No ranked opportunities yet. Add properties and run <span className="font-semibold">Recalculate All</span>.
        </p>
      ) : (
        <div className="mt-4 overflow-hidden rounded-xl border border-slate-100">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">#</th>
                <th className="text-left px-4 py-2.5">Property</th>
                <th className="text-left px-4 py-2.5">Heat</th>
                <th className="text-right px-4 py-2.5">Motivation</th>
                <th className="text-right px-4 py-2.5">Acq. Score</th>
                <th className="text-right px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((item) => {
                const heat = HEAT_META[item.heat] || HEAT_META.cold;
                return (
                  <tr key={item.property_id}
                      onClick={() => onOpen(item.property_id)}
                      data-testid={`acq-lead-row-${item.property_id}`}
                      className="hover:bg-amber-50/40 cursor-pointer transition-colors">
                    <td className="px-4 py-2.5 text-[13px] font-bold text-slate-500 tabular-nums">
                      {item.rank}
                    </td>
                    <td className="px-4 py-2.5 text-[13px] font-semibold text-slate-800">
                      {item.property_address || "Untitled property"}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${heat.bg} ${heat.text}`}>
                        <Flame className="w-3 h-3" /> {heat.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-[13px] font-bold text-slate-700 tabular-nums">
                      {item.motivation_score}
                    </td>
                    <td className="px-4 py-2.5 text-right text-[13px] font-bold text-slate-700 tabular-nums">
                      {item.acquisition_score}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── detail drawer ───────────────────────────── */

function SellerDetailDrawer({ propertyId, onClose, onActionLogged }) {
  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/v3/acquisition-intel/${propertyId}`);
        if (!cancelled) setIntel(data);
      } catch {
        toast.error("Failed to load seller intelligence.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [propertyId]);

  const takeAction = useCallback(async (kind) => {
    if (!intel || busy) return;
    setBusy(true);
    try {
      await api.post(`/v3/acquisition-intel/${propertyId}/next-action`, {
        action: intel.next_action.kind,
        kind,
      });
      toast.success(kind === "action_taken"
        ? "Action logged — nice work."
        : "Snoozed — we'll re-surface later.");
      onActionLogged?.();
    } catch { toast.error("Couldn't log action."); }
    finally { setBusy(false); }
  }, [propertyId, intel, busy, onActionLogged]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      role="dialog"
      aria-modal="true"
      data-testid="acq-detail-drawer"
    >
      <button type="button" onClick={onClose} aria-label="Close"
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
      <div className="relative w-full max-w-2xl bg-white overflow-y-auto shadow-2xl border-l border-slate-200">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-black tracking-widest uppercase text-emerald-600">
              Seller Intelligence
            </div>
            <h2 className="text-lg font-bold text-slate-900">
              {intel?.property_address || "Loading…"}
            </h2>
          </div>
          <button type="button" onClick={onClose}
                  data-testid="acq-detail-close"
                  className="rounded-full hover:bg-slate-100 p-2">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {loading || !intel ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Score row */}
            <div className="grid grid-cols-2 gap-3">
              <ScoreTile label="Motivation" score={intel.motivation.total} band={intel.motivation.band} />
              <ScoreTile label="Acquisition" score={intel.acquisition.total} band={intel.acquisition.band} heat={intel.heat} />
            </div>

            {/* Next Best Action — big CTA */}
            <NextActionCTA action={intel.next_action} onTake={() => takeAction("action_taken")} busy={busy} />

            {/* Contact recommendation */}
            <ContactRecCard contact={intel.contact} />

            {/* Timeline prediction */}
            <TimelineCard prediction={intel.prediction} />

            {/* Explainability — motivation reasons */}
            <ReasonsCard title="Why this motivation score" reasons={intel.motivation.reasons} />

            {/* Explainability — acquisition reasons */}
            <ReasonsCard title="Why this acquisition score" reasons={intel.acquisition.reasons} />

            {/* Snooze / dismiss */}
            <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
              <button type="button" onClick={() => takeAction("action_snoozed")}
                      data-testid="acq-action-snooze"
                      className="flex-1 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold py-2 hover:bg-slate-50">
                Snooze this lead
              </button>
              <button type="button" onClick={() => takeAction("action_dismissed")}
                      data-testid="acq-action-dismiss"
                      className="flex-1 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold py-2 hover:bg-slate-50">
                Not right now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ScoreTile({ label, score, band, heat }) {
  const bandLabel = BAND_META[band]?.label || band;
  const heatMeta = heat ? HEAT_META[heat] : null;
  return (
    <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-white to-amber-50/30 p-4">
      <div className="text-[10px] font-black tracking-widest uppercase text-slate-500">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="text-4xl font-bold text-slate-900 tabular-nums">{score}</div>
        <div className="text-xs text-slate-400 font-semibold uppercase tracking-widest">/ 100</div>
      </div>
      <div className="mt-1 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{bandLabel}</span>
        {heatMeta && (
          <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${heatMeta.bg} ${heatMeta.text}`}>
            <Flame className="w-2.5 h-2.5" /> {heatMeta.label}
          </span>
        )}
      </div>
    </div>
  );
}

function NextActionCTA({ action, onTake, busy }) {
  const Icon = ACTION_ICON[action.kind] || ArrowRight;
  return (
    <div className="rounded-xl border-2 border-amber-300/60 bg-gradient-to-br from-amber-50 to-white p-4 shadow-sm">
      <div className="text-[10px] font-black tracking-widest uppercase text-amber-600">Next Best Action</div>
      <div className="mt-1 text-lg font-bold text-slate-900">{action.label}</div>
      <p className="mt-1 text-[12px] text-slate-600 leading-relaxed">{action.reasoning}</p>
      <button type="button" onClick={onTake} disabled={busy}
              data-testid="acq-action-take"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-lg text-white text-sm font-bold py-2.5 disabled:opacity-60"
              style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #EA580C)" }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
        Mark as done
      </button>
    </div>
  );
}

function ContactRecCard({ contact }) {
  const MethodIcon = {
    call: Phone, sms: MessageSquare, email: Mail,
    direct_mail: Send, door_knock: Target,
  }[contact.method] || Phone;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] font-black tracking-widest uppercase text-emerald-600">Recommended Contact</div>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-500/30 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest">
          <MethodIcon className="w-3 h-3" /> {contact.method.replace(/_/g, " ")}
        </span>
        <span className="text-[11px] font-semibold text-slate-500">{contact.window}</span>
      </div>
      <p className="mt-2 text-[12px] text-slate-600 leading-relaxed">{contact.reasoning}</p>
      {contact.ai_talking_points?.length > 0 && (
        <ul className="mt-2 space-y-1 pl-4 list-disc text-[12px] text-slate-700 leading-relaxed">
          {contact.ai_talking_points.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      )}
    </div>
  );
}

function TimelineCard({ prediction }) {
  const buckets = [
    { key: "p_7_day",     label: "7 days"     },
    { key: "p_30_day",    label: "30 days"    },
    { key: "p_90_day",    label: "90 days"    },
    { key: "p_6_month",   label: "6 months"   },
    { key: "p_long_term", label: "Long-term"  },
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] font-black tracking-widest uppercase text-emerald-600">Predicted Timeline</div>
      <div className="mt-3 space-y-2">
        {buckets.map(({ key, label }) => {
          const v = prediction[key] || 0;
          const pct = Math.round(v * 100);
          return (
            <div key={key} className="flex items-center gap-3">
              <div className="w-20 text-[11px] font-bold uppercase tracking-widest text-slate-500">
                {label}
              </div>
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full bg-emerald-500"
                     style={{ width: `${pct}%` }} />
              </div>
              <div className="w-10 text-right text-[12px] font-bold text-slate-600 tabular-nums">
                {pct}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReasonsCard({ title, reasons }) {
  if (!reasons || reasons.length === 0) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] font-black tracking-widest uppercase text-emerald-600">{title}</div>
      <ul className="mt-3 space-y-1.5">
        {reasons.map((r, i) => (
          <li key={i} className="flex items-start gap-3 text-[13px]">
            <span className={`shrink-0 w-9 text-right font-bold tabular-nums ${
              r.weight >= 0 ? "text-emerald-600" : "text-red-500"
            }`}>
              {r.weight >= 0 ? `+${r.weight}` : r.weight}
            </span>
            <span className="text-slate-700">{r.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
